// src/components/HotelResults.jsx — Broadway Holidays
// Booking.com API — Hotels only, no flights
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SearchWidget from "./SearchWidget";
import {
  searchHotels,
  searchCarRentals, searchCarLocation,
  searchTaxis, searchTaxiLocation,
  searchAttractions, searchAttractionLocation,
  _s, fixPhoto, ratingBubbles,
} from "../services/hotelApi";

const PJS  = {fontFamily:"'Plus Jakarta Sans',sans-serif"};
const DS   = {fontFamily:"'DM Sans',sans-serif"};
const NAVY = "#0B1F3A", NAVYL = "#1a3a5c";
const GRN  = "#059669", GRNL = "#d1fae5", GRND = "#065f46";
const RED  = "#e8390e", REDL = "#fef2f2";
const BDR  = "#e2e8f0", BG = "#f0f4f8";
const AMB  = "#d97706";

const MEAL_CAT  = {"All Inclusive":"9","Half Board":"3","Full Board":"4","Bed & Breakfast":"1","Self Catering":"12","Room Only":"0"};
const MEAL_COLS = {"All Inclusive":"#064e3b","Half Board":"#92400e","Full Board":"#4c1d95","Bed & Breakfast":"#1e3a8a","Self Catering":"#374151","Room Only":"#4b5563"};

// Hotel data extractors
const gId      = h => h?.property?.hotel_id||h?.hotel_id||h?.property?.id||h?.id;
const gName    = h => _s(h?.property?.name||h?.hotel_name||h?.name)||"Hotel";
const gCity    = h => _s(h?.property?.wishlistName||h?.city||h?.cityName||"");
const gStar    = h => { const v=h?.property?.class??h?.property?.stars??h?.class??h?.stars??0; const n=Number(v); return isNaN(n)?0:Math.floor(n); };
const gRtg     = h => Number(h?.property?.reviewScore||h?.review_score||h?.rating||0);
const gRevs    = h => Number(h?.property?.reviewCount||h?.review_nr||0);
const gRaw     = h => Number(h?.property?.priceBreakdown?.grossPrice?.value||h?.property?.priceBreakdown?.allInclusiveAmount?.value||h?.composite_price_breakdown?.gross_amount?.value||h?.min_total_price||0);
const gPho     = h => { const u=Array.isArray(h?.property?.photoUrls)&&h.property.photoUrls.length?h.property.photoUrls[0]:(h?.main_photo_url||null); return fixPhoto(u)||u||null; };
const gFreeCan = h => !!(h?.property?.priceBreakdown?.benefits?.some?.(b=>_s(b?.label||b?.name||"").toLowerCase().includes("free cancel"))||h?.free_cancellation);

const I = {
  Filter: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  Star:   ({s=13,f="#f59e0b"}) => <svg width={s} height={s} viewBox="0 0 24 24" fill={f}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  Pin:    ({s=12}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Cal:    ({s=12}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Check:  ({s=10,c="#fff"}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  X:      ({s=10}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Chev:   ({s=10,up=false}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{transform:up?"rotate(180deg)":"none",transition:"transform .2s"}}><polyline points="6 9 12 15 18 9"/></svg>,
  Right:  ({s=12,c="currentColor"}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  WApp:   ({s=14}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.985-1.349A9.954 9.954 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>,
  Phone:  ({s=12}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.91-1.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  Food:   ({s=14}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>,
  Smile:  ({s=14}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>,
  Money:  ({s=14}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
};

function Bubbles({score,sz=10}){
  if(!score)return null;
  const n=ratingBubbles(score),full=Math.floor(n),half=(n%1)>=.25&&(n%1)<.75?1:0,empty=5-full-half;
  return <span style={{display:"inline-flex",alignItems:"center",gap:1.5}}>
    {[...Array(full)].map((_,i)=><span key={"f"+i} style={{width:sz,height:sz,borderRadius:"50%",background:"#34e0a1",display:"inline-block",flexShrink:0}}/>)}
    {half===1&&<span style={{width:sz,height:sz,borderRadius:"50%",background:"linear-gradient(90deg,#34e0a1 50%,#d1fae5 50%)",display:"inline-block",flexShrink:0}}/>}
    {[...Array(empty)].map((_,i)=><span key={"e"+i} style={{width:sz,height:sz,borderRadius:"50%",background:"#d1fae5",border:"1.5px solid #34e0a1",display:"inline-block",flexShrink:0,boxSizing:"border-box"}}/>)}
  </span>;
}

function Skel(){
  const B=({h=12,w="100%",r=4})=><div style={{height:h,width:w,borderRadius:r,background:"linear-gradient(90deg,#f1f5f9 25%,#e8edf4 50%,#f1f5f9 75%)",backgroundSize:"200% 100%",animation:"sk 1.4s infinite"}}/>;
  return <div style={{display:"flex",background:"#fff",border:`1px solid ${BDR}`,borderRadius:14,marginBottom:10,overflow:"hidden"}}>
    <div style={{width:230,background:"linear-gradient(90deg,#e8edf4 25%,#dde4ed 50%,#e8edf4 75%)",backgroundSize:"200% 100%",animation:"sk 1.4s infinite",flexShrink:0,minHeight:190}}/>
    <div style={{flex:1,padding:"16px 14px",display:"flex",flexDirection:"column",gap:9}}><B h={17} w="60%"/><B h={11} w="35%"/><B h={11} w="50%"/><B h={52} r={8}/></div>
    <div style={{width:180,padding:14,borderLeft:`1px solid ${BDR}`,display:"flex",flexDirection:"column",gap:8}}><B h={11} w="70%"/><B h={26} r={5}/><B h={34} r={8}/><B h={32} r={8}/></div>
  </div>;
}

/* ── Hotel Card ────────────────────────────────────────────── */
function HotelCard({h,city,ci,co,guests,activeMeal}){
  const nav = useNavigate();
  const [imgErr,setImg] = useState(false);
  const id=gId(h), name=gName(h), hCity=gCity(h);
  const photo   = imgErr ? null : gPho(h);
  const stars   = gStar(h), rating=gRtg(h), revs=gRevs(h);
  const freeCan = gFreeCan(h);
  const nights  = ci&&co ? Math.max(1,Math.round((new Date(co)-new Date(ci))/86400000)) : 7;
  const gCnt    = Math.max(1,Number(guests)||2);
  const raw     = gRaw(h);
  const hotelPP = raw>0 ? Math.ceil((raw/gCnt)*1.05) : null;
  const deposit = hotelPP ? Math.max(19,Math.round(hotelPP*0.08)) : null;
  const ratingWord = rating>=9.2?"Exceptional":rating>=8.5?"Excellent":rating>=8?"Very Good":rating>=7?"Good":rating>=6?"Pleasant":rating>0?"Reviewed":"";

  const go = () => id && nav(
    `/hotel/${id}?checkIn=${ci}&checkOut=${co}&guests=${guests}&rooms=1` +
    `&board=${encodeURIComponent(activeMeal||"")}` +
    `&city=${encodeURIComponent(city||hCity)}` +
    `&hotelName=${encodeURIComponent(name)}` +
    `${hotelPP?"&price="+hotelPP:""}` +
    `&nights=${nights}`
  );

  const waTxt = encodeURIComponent(`Hi, I'm interested in ${name}${city?" in "+city:""} — hotel from £${hotelPP||"?"}pp`);
  const fmtD  = d => { try{return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});}catch{return d||"";} };

  return(
    <div style={{background:"#fff",border:`1px solid ${BDR}`,borderRadius:14,marginBottom:10,overflow:"hidden",boxShadow:"0 2px 8px rgba(11,31,58,.05)",transition:"box-shadow .2s,transform .2s"}}
      onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 8px 28px rgba(11,31,58,.12)";e.currentTarget.style.transform="translateY(-1px)";}}
      onMouseLeave={e=>{e.currentTarget.style.boxShadow="0 2px 8px rgba(11,31,58,.05)";e.currentTarget.style.transform="none";}}>
      <div className="hcard-inner" style={{display:"flex"}}>

        {/* Photo */}
        <div className="hcard-img" style={{width:230,flexShrink:0,position:"relative",overflow:"hidden",background:"#e2e8f0",cursor:"pointer"}} onClick={go}>
          {photo
            ?<img src={photo} alt={name} style={{width:"100%",height:"100%",minHeight:190,objectFit:"cover",display:"block",transition:"transform .4s"}}
               onError={()=>setImg(true)} loading="lazy"
               onMouseEnter={e=>e.target.style.transform="scale(1.05)"}
               onMouseLeave={e=>e.target.style.transform="none"}/>
            :<div style={{minHeight:190,background:`linear-gradient(135deg,${NAVY},${NAVYL})`,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontSize:36}}>🏨</span></div>
          }
          {stars>0&&<div style={{position:"absolute",top:0,left:0,background:"rgba(11,31,58,.8)",padding:"5px 8px",display:"flex",alignItems:"center",gap:2}}>
            {[...Array(Math.min(5,stars))].map((_,i)=><I.Star key={i} s={10}/>)}
          </div>}
          {freeCan&&<div style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(5,150,105,.85)",padding:"3px 8px",textAlign:"center"}}>
            <span style={{fontSize:9,fontWeight:700,color:"#fff",...DS}}>✓ FREE CANCELLATION</span>
          </div>}
        </div>

        {/* Middle */}
        <div style={{flex:1,minWidth:0,padding:"14px 14px 12px",display:"flex",flexDirection:"column"}}>
          <div style={{flex:1,cursor:"pointer"}} onClick={go}>
            <h3 style={{fontSize:15,fontWeight:900,color:NAVY,margin:"0 0 4px",lineHeight:1.3,...PJS}}>{name}</h3>
            {(hCity||city)&&<div style={{display:"flex",alignItems:"center",gap:4,fontSize:12,color:"#64748b",marginBottom:5,...DS}}><I.Pin s={10}/>{hCity||city}</div>}
            {rating>0&&<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,flexWrap:"wrap"}}>
              <span style={{background:rating>=8.5?"#003580":rating>=7?"#006ce4":"#64748b",color:"#fff",borderRadius:"4px 4px 4px 0",padding:"2px 7px",fontSize:11,fontWeight:800,...DS}}>{rating.toFixed(1)}</span>
              {ratingWord&&<span style={{fontSize:12,fontWeight:700,color:"#334155",...DS}}>{ratingWord}</span>}
              <Bubbles score={rating} sz={10}/>
              {revs>0&&<span style={{fontSize:11,color:"#94a3b8",...DS}}>{revs.toLocaleString()} reviews</span>}
            </div>}
            {activeMeal&&<div style={{marginBottom:5}}>
              <span style={{display:"inline-block",padding:"2px 8px",borderRadius:5,fontSize:11,fontWeight:700,background:MEAL_COLS[activeMeal]||"#374151",color:"#fff",...DS}}>{activeMeal}</span>
            </div>}
            {ci&&<div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#64748b",...DS}}><I.Cal s={10}/>{fmtD(ci)} · {nights} night{nights!==1?"s":""}</div>}
          </div>

          <div style={{marginTop:8,padding:"7px 10px",background:"#f8fafc",border:`1px solid ${BDR}`,borderRadius:7,display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:16}}>🏨</span>
            <span style={{fontSize:11,color:"#64748b",...DS}}>Hotel · <strong>per person price</strong></span>
          </div>
        </div>

        {/* Price */}
        <div className="hcard-price" style={{width:185,flexShrink:0,borderLeft:`1px solid ${BDR}`,padding:"14px 13px",display:"flex",flexDirection:"column",background:"#fafbfc"}}>
          <div style={{flex:1}}>
            {deposit&&<div style={{fontSize:10,color:"#64748b",marginBottom:3,...DS}}>Deposit from <strong style={{color:NAVY}}>£{deposit}pp</strong></div>}
            {hotelPP
              ?<>
                <div style={{display:"flex",alignItems:"baseline",gap:2,marginBottom:1}}>
                  <span style={{fontSize:10,color:"#64748b",...DS}}>From</span>
                  <span style={{fontSize:27,fontWeight:900,color:NAVY,lineHeight:1,marginLeft:4,...PJS}}>£{hotelPP}</span>
                </div>
                <div style={{fontSize:11,color:"#64748b",...DS}}>per person</div>
                <div style={{fontSize:9,color:"#94a3b8",marginTop:1,...DS}}>hotel only</div>
              </>
              :<div style={{fontSize:12,color:"#94a3b8",...DS}}>Price on request</div>
            }
          </div>
          <div style={{marginTop:"auto",paddingTop:10,display:"flex",gap:4}}>
            <a href={`https://wa.me/447487672075?text=${waTxt}`} target="_blank" rel="noreferrer"
              onClick={e=>e.stopPropagation()}
              style={{flex:1,padding:"7px 0",background:"#22c55e",color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:3,textDecoration:"none"}}>
              <I.WApp s={12}/>WA
            </a>
            <a href="tel:02046006140" onClick={e=>e.stopPropagation()}
              style={{flex:1,padding:"7px 0",background:"#1d4ed8",color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:3,textDecoration:"none"}}>
              <I.Phone s={11}/>Call
            </a>
          </div>
          <button onClick={go}
            style={{width:"100%",padding:"10px 0",background:NAVY,color:"#fff",border:"none",borderRadius:9,fontSize:13,fontWeight:700,cursor:"pointer",marginTop:5,...DS,display:"flex",alignItems:"center",justifyContent:"center",gap:5,transition:"background .15s"}}
            onMouseEnter={e=>e.currentTarget.style.background=NAVYL}
            onMouseLeave={e=>e.currentTarget.style.background=NAVY}>
            View Hotel <I.Right s={12} c="#fff"/>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Sidebar Filter ────────────────────────────────────────── */
function CBox({label,checked,onChange,count,color}){
  return <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",padding:"5px 2px",fontSize:13,...DS,userSelect:"none"}} onClick={onChange}>
    <span style={{width:18,height:18,border:checked?"none":`1.5px solid ${BDR}`,borderRadius:4,background:checked?(color||NAVY):"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,minWidth:18,transition:"all .15s",pointerEvents:"none"}}>
      {checked&&<I.Check s={10}/>}
    </span>
    <span style={{flex:1,color:"#1e293b",fontWeight:checked?600:400,lineHeight:1.35,pointerEvents:"none"}}>{label}</span>
    {count!=null&&count>0&&<span style={{fontSize:11,color:"#94a3b8",minWidth:24,textAlign:"right",...DS,pointerEvents:"none"}}>{count}</span>}
  </label>;
}

function RadioRow({label,value,current,onChange,count}){
  const on=current===value;
  return <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",padding:"5px 2px",fontSize:13,...DS,userSelect:"none"}} onClick={()=>onChange(value)}>
    <span style={{width:18,height:18,border:on?"none":`1.5px solid ${BDR}`,borderRadius:"50%",background:on?NAVY:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,minWidth:18,transition:"all .15s",pointerEvents:"none"}}>
      {on&&<span style={{width:7,height:7,borderRadius:"50%",background:"#fff",display:"block"}}/>}
    </span>
    <span style={{flex:1,color:"#1e293b",fontWeight:on?600:400,pointerEvents:"none"}}>{label}</span>
    {count!=null&&count>0&&<span style={{fontSize:11,color:"#94a3b8",minWidth:24,textAlign:"right",...DS,pointerEvents:"none"}}>{count}</span>}
  </label>;
}

function Sec({icon,title,children,open:init=true,badge=0}){
  const [open,setOpen]=useState(init);
  return <div style={{borderBottom:`1px solid ${BDR}`}}>
    <button onClick={()=>setOpen(v=>!v)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",background:"none",border:"none",cursor:"pointer",padding:"11px 0",fontFamily:"inherit"}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{color:"#64748b",display:"flex"}}>{icon}</span>
        <div style={{fontSize:13,fontWeight:700,color:NAVY,...DS}}>{title}{badge>0&&<span style={{marginLeft:6,background:RED,color:"#fff",borderRadius:10,fontSize:10,fontWeight:700,padding:"1px 5px",...DS}}>{badge}</span>}</div>
      </div>
      <I.Chev s={10} up={open}/>
    </button>
    {open&&<div style={{paddingBottom:10}}>{children}</div>}
  </div>;
}

function Sidebar({filters,setFilters,starCounts,reviewCounts,onClose,city,ci,co,guests}){
  const tog  = (k,v) => setFilters(p=>({...p,[k]:p[k].includes(v)?p[k].filter(x=>x!==v):[...p[k],v]}));
  const setR = v => setFilters(p=>({...p,rating:v}));
  const reset = () => setFilters({meals:[],stars:[],rating:"0",price:"",freeCan:false});
  const active = filters.meals.length+filters.stars.length+(filters.rating!=="0"?1:0)+(filters.price?1:0)+(filters.freeCan?1:0);

  return <div style={{background:"#fff",border:`1px solid ${BDR}`,borderRadius:14,overflow:"hidden",position:"sticky",top:80,maxHeight:"calc(100vh - 96px)",overflowY:"auto"}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",borderBottom:`1px solid ${BDR}`,background:"#f8fafc"}}>
      <div style={{display:"flex",alignItems:"center",gap:7}}>
        <I.Filter/>
        <span style={{fontSize:14,fontWeight:800,color:NAVY,...DS}}>Filter by</span>
        {active>0&&<span style={{background:NAVY,color:"#fff",borderRadius:20,fontSize:11,fontWeight:700,padding:"1px 7px",...DS}}>{active}</span>}
      </div>
      <div style={{display:"flex",gap:8}}>
        {active>0&&<button onClick={reset} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",fontWeight:600,...DS}}>Reset all</button>}
        {onClose&&<button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",display:"flex",padding:2}}><I.X s={13}/></button>}
      </div>
    </div>
    <div style={{padding:"0 14px"}}>
      {/* Free cancellation */}
      <div style={{padding:"10px 0",borderBottom:`1px solid ${BDR}`}}>
        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,...DS,userSelect:"none",padding:"3px 0"}} onClick={()=>setFilters(p=>({...p,freeCan:!p.freeCan}))}>
          <span style={{width:18,height:18,border:filters.freeCan?"none":`1.5px solid ${BDR}`,borderRadius:4,background:filters.freeCan?"#059669":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s",pointerEvents:"none"}}>
            {filters.freeCan&&<I.Check s={10}/>}
          </span>
          <span style={{color:"#1e293b",fontWeight:filters.freeCan?600:400,pointerEvents:"none"}}>🟢 Free cancellation only</span>
        </label>
      </div>

      {/* Budget */}
      <Sec icon={<I.Money s={14}/>} title="Budget (per person)">
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:12,...DS}}>
          <span style={{color:"#64748b"}}>Any</span>
          <span style={{fontWeight:700,color:NAVY}}>{filters.price?`Up to £${filters.price}pp`:"Any price"}</span>
        </div>
        <input type="range" min="50" max="2000" step="25" value={filters.price||2000}
          onChange={e=>setFilters(p=>({...p,price:Number(e.target.value)===2000?"":e.target.value}))}
          style={{width:"100%",accentColor:NAVY,cursor:"pointer",height:4}}/>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#94a3b8",marginTop:3,...DS}}><span>£50pp</span><span>£2,000+</span></div>
      </Sec>

      {/* Board basis */}
      <Sec icon={<I.Food s={14}/>} title="Board basis" badge={filters.meals.length}>
        {Object.keys(MEAL_CAT).map(name=>(
          <CBox key={name}
            label={<span style={{display:"flex",alignItems:"center",gap:6}}><span style={{width:8,height:8,borderRadius:2,background:MEAL_COLS[name]||"#374151",flexShrink:0,display:"inline-block"}}/>{name}</span>}
            color={MEAL_COLS[name]} count={null}
            checked={filters.meals.includes(name)}
            onChange={()=>tog("meals",name)}/>
        ))}
      </Sec>

      {/* Star rating */}
      <Sec icon={<I.Star s={14}/>} title="Star rating" badge={filters.stars.length}>
        {[5,4,3,2,1].map(n=>(
          <CBox key={n}
            label={<span style={{display:"flex",alignItems:"center",gap:4}}>{[...Array(n)].map((_,i)=><I.Star key={i} s={11}/>)}<span style={{fontSize:12,color:"#64748b",...DS}}>{n} star{n>1?"s":""}</span></span>}
            count={starCounts[String(n)]||0}
            checked={filters.stars.includes(String(n))}
            onChange={()=>tog("stars",String(n))}/>
        ))}
      </Sec>

      {/* Guest rating */}
      <Sec icon={<I.Smile s={14}/>} title="Guest rating" badge={filters.rating!=="0"?1:0}>
        {[{v:"0",l:"Any"},{v:"9",l:"Exceptional 9+"},{v:"8",l:"Excellent 8+"},{v:"7",l:"Very Good 7+"},{v:"6",l:"Good 6+"}]
          .map(({v,l})=><RadioRow key={v} label={l} value={v} current={filters.rating} onChange={setR} count={reviewCounts[v]}/>)}
      </Sec>
    </div>
  </div>;
}

/* ── MAIN PAGE ──────────────────────────────────────────────── */
const TOP = ["Tenerife","Lanzarote","Antalya"];

export default function HotelResults(){
  const [sp]  = useSearchParams();
  const city  = (sp.get("city")||"").trim();
  const ci    = sp.get("checkIn")||"";
  const co    = sp.get("checkOut")||"";
  const guests = sp.get("guests")||2;
  const isAll  = !city;
  // Exact place id when the customer picked a suggestion (city, region, country…)
  const destId   = sp.get("dest_id")||"";
  const destType = sp.get("dest_type")||"";
  const exact    = destId&&destType ? {dest_id:destId,search_type:destType} : {};

  const [allRaw,   setAllRaw]  = useState([]);
  const [loading,  setLoad]    = useState(true);
  const [error,    setError]   = useState("");
  const [pct,      setPct]     = useState(0);
  const [sort,     setSort]    = useState("popularity");
  const [filters,  setFilters] = useState({meals:[],stars:[],rating:"0",price:"",freeCan:false});
  const [mealLoad, setMealLd]  = useState(false);
  const [mobileF,  setMobF]    = useState(false);
  const prevMeals = useRef([]);

  // Initial load
  useEffect(()=>{
    let gone=false;
    setLoad(true);setError("");setAllRaw([]);setPct(0);
    setFilters({meals:[],stars:[],rating:"0",price:"",freeCan:false});
    const search = d => searchHotels(d,ci,co,Number(guests),1,{sort_by:"popularity",...(d===city?exact:{})}).catch(()=>[]);
    if(isAll){
      let done=0;
      Promise.allSettled(TOP.map(d=>search(d).then(list=>{done++;if(!gone){setAllRaw(p=>[...p,...list.map(h=>({hotel:h,dest:d}))]);setPct(Math.round(done/TOP.length*100));}})))
        .then(()=>{if(!gone)setLoad(false);});
    }else{
      search(city).then(list=>{if(!gone){setAllRaw(list.map(h=>({hotel:h,dest:city})));setLoad(false);setPct(100);}})
        .catch(e=>{if(!gone){setError(e.message||"Failed");setLoad(false);}});
    }
    return()=>{gone=true;};
  },[city,ci,co,guests,isAll,destId,destType]);

  // Meal filter re-search
  useEffect(()=>{
    const prev=prevMeals.current,curr=filters.meals;
    if(JSON.stringify(prev)===JSON.stringify(curr))return;
    prevMeals.current=curr;
    let gone=false;
    setMealLd(true);setAllRaw([]);
    const mealIds=curr.map(n=>MEAL_CAT[n]).filter(Boolean).map(Number);
    const catFilter=mealIds.map(id=>`mealplan-${id}`).join(",");
    const opts=curr.length===0?{sort_by:"popularity"}:{sort_by:"popularity",meal_types:mealIds,categories_filter:catFilter,_bypassCache:true};
    const search = d => searchHotels(d,ci,co,Number(guests),1,{...opts,...(d===city?exact:{})}).catch(()=>[]);
    if(isAll){
      let done=0;
      Promise.allSettled(TOP.map(d=>search(d).then(list=>{done++;if(!gone)setAllRaw(p=>[...p,...list.map(h=>({hotel:h,dest:d}))]);})))
        .then(()=>{if(!gone)setMealLd(false);});
    }else{
      search(city).then(list=>{if(!gone){setAllRaw(list.map(h=>({hotel:h,dest:city})));setMealLd(false);}})
        .catch(()=>{if(!gone)setMealLd(false);});
    }
    return()=>{gone=true;};
  },[JSON.stringify(filters.meals)]); // eslint-disable-line

  const displayed = useMemo(()=>{
    let d=[...allRaw];
    if(filters.freeCan) d=d.filter(({hotel:h})=>gFreeCan(h));
    if(filters.stars.length) d=d.filter(({hotel:h})=>{const s=gStar(h);return s>0&&filters.stars.includes(String(s));});
    if(filters.rating&&filters.rating!=="0"){const min=Number(filters.rating);d=d.filter(({hotel:h})=>gRtg(h)>=min);}
    if(filters.price&&Number(filters.price)<2000){
      const mx=Number(filters.price);
      d=d.filter(({hotel:h})=>{const r=gRaw(h);if(!r)return true;return Math.ceil((r/Math.max(1,Number(guests)))*1.05)<=mx;});
    }
    if(sort==="price")         d.sort((a,b)=>gRaw(a.hotel)-gRaw(b.hotel));
    else if(sort==="review_score") d.sort((a,b)=>gRtg(b.hotel)-gRtg(a.hotel));
    else if(sort==="stars")    d.sort((a,b)=>gStar(b.hotel)-gStar(a.hotel));
    return d;
  },[allRaw,filters.freeCan,filters.stars,filters.rating,filters.price,sort,guests]);

  const starCounts   = useMemo(()=>{const c={};allRaw.forEach(({hotel:h})=>{const s=gStar(h);if(s>0)c[String(s)]=(c[String(s)]||0)+1;});return c;},[allRaw]);
  const reviewCounts = useMemo(()=>{const c={"9":0,"8":0,"7":0,"6":0};allRaw.forEach(({hotel:h})=>{const r=gRtg(h);if(r>=9)c["9"]++;else if(r>=8)c["8"]++;else if(r>=7)c["7"]++;else if(r>=6)c["6"]++;});return c;},[allRaw]);

  const chips = [
    ...filters.meals.map(v=>({key:`m-${v}`,label:v,rm:()=>setFilters(p=>({...p,meals:p.meals.filter(x=>x!==v)}))})),
    ...filters.stars.map(v=>({key:`s-${v}`,label:`${v}★`,rm:()=>setFilters(p=>({...p,stars:p.stars.filter(x=>x!==v)}))})),
    ...(filters.rating!=="0"?[{key:"r",label:{"9":"Exceptional 9+","8":"Excellent 8+","7":"Very Good 7+","6":"Good 6+"}[filters.rating]||"",rm:()=>setFilters(p=>({...p,rating:"0"}))}]:[]),
    ...(filters.price?[{key:"p",label:`Up to £${filters.price}pp`,rm:()=>setFilters(p=>({...p,price:""}))}]:[]),
    ...(filters.freeCan?[{key:"fc",label:"Free cancellation",rm:()=>setFilters(p=>({...p,freeCan:false}))}]:[]),
  ];

  const isLoading = loading||mealLoad;
  const nights    = ci&&co ? Math.max(1,Math.round((new Date(co)-new Date(ci))/86400000)) : null;
  const fmtDate   = d => { try{return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});}catch{return d;} };
  const activeMeal = filters.meals[0]||"";

  return(
    <>
      <Navbar/>

      {/* Search bar */}
      <div style={{background:NAVY,padding:"12px 20px 10px"}}>
        <div style={{maxWidth:1380,margin:"0 auto"}}>
          <nav style={{display:"flex",gap:5,alignItems:"center",marginBottom:7,fontSize:12,...DS}}>
            <Link to="/" style={{color:"rgba(255,255,255,.6)",textDecoration:"none"}}>Home</Link>
            <I.Right s={10} c="rgba(255,255,255,.4)"/>
            <span style={{color:"rgba(255,255,255,.9)"}}>{isAll?"All Hotels":`Hotels in ${city}`}</span>
          </nav>
          <SearchWidget compact/>
        </div>
      </div>

      <div style={{background:BG,minHeight:"100vh"}}>
        <div style={{maxWidth:1380,margin:"0 auto",padding:"18px 18px 80px",display:"flex",gap:20,alignItems:"flex-start"}}>

          {/* Sidebar */}
          <div style={{width:280,flexShrink:0}} className="res-sidebar">
            <Sidebar filters={filters} setFilters={setFilters} starCounts={starCounts} reviewCounts={reviewCounts} city={city} ci={ci} co={co} guests={Number(guests)}/>
          </div>

          {/* Mobile filter drawer */}
          {mobileF&&(
            <div style={{position:"fixed",inset:0,zIndex:3000,display:"flex"}}>
              <div onClick={()=>setMobF(false)} style={{flex:1,background:"rgba(11,31,58,.55)"}}/>
              <div style={{width:320,background:"#fff",overflowY:"auto"}}>
                <Sidebar filters={filters} setFilters={setFilters} starCounts={starCounts} reviewCounts={reviewCounts} city={city} ci={ci} co={co} guests={Number(guests)} onClose={()=>setMobF(false)}/>
              </div>
            </div>
          )}

          {/* Results */}
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10,flexWrap:"wrap",gap:8}}>
              <div>
                <h1 style={{fontSize:20,fontWeight:900,color:NAVY,margin:"0 0 2px",...PJS}}>
                  {isLoading&&allRaw.length===0 ? "Searching hotels…"
                    : `${displayed.length.toLocaleString()} hotel${displayed.length!==1?"s":""} found`}
                  {!isAll&&` in ${city}`}
                  {mealLoad&&<span style={{fontSize:12,color:GRN,...DS,marginLeft:8,fontWeight:600}}>● Filtering…</span>}
                </h1>
                {ci&&<p style={{fontSize:12,color:"#64748b",margin:0,...DS}}>{fmtDate(ci)}{nights?` · ${nights} nights`:""} · {guests} guest{Number(guests)!==1?"s":""} · <strong>Hotel only</strong></p>}
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <button onClick={()=>setMobF(true)} className="mob-btn" style={{display:"none",alignItems:"center",gap:5,padding:"8px 12px",background:"#fff",border:`1.5px solid ${BDR}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",...DS}}>
                  <I.Filter/>Filters{chips.length>0?` (${chips.length})`:""}</button>
                <select value={sort} onChange={e=>setSort(e.target.value)} style={{padding:"8px 12px",borderRadius:8,border:`1.5px solid ${BDR}`,fontSize:13,background:"#fff",cursor:"pointer",fontFamily:"inherit",fontWeight:600,color:NAVY,outline:"none"}}>
                  <option value="popularity">Most popular</option>
                  <option value="review_score">Top rated</option>
                  <option value="price">Price: low to high</option>
                  <option value="stars">Stars: high to low</option>
                </select>
                {chips.length>0&&<button onClick={()=>setFilters({meals:[],stars:[],rating:"0",price:"",freeCan:false})} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 12px",borderRadius:20,cursor:"pointer",fontSize:12,background:REDL,color:"#dc2626",border:"1px solid #fecaca",fontWeight:700,fontFamily:"inherit",...DS}}>
                  <I.X s={10}/>Clear all</button>}
              </div>
            </div>

            {/* Active filter chips */}
            {chips.length>0&&(
              <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:10}}>
                {chips.map(c=>(
                  <button key={c.key} onClick={c.rm} style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:20,background:NAVY,color:"#fff",fontSize:12,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",...DS}}>
                    {c.label}<I.X s={8}/>
                  </button>
                ))}
              </div>
            )}

            {isLoading&&allRaw.length===0&&[1,2,3,4,5].map(i=><Skel key={i}/>)}

            {isLoading&&allRaw.length>0&&(
              <div style={{background:GRNL,border:"1px solid #a7f3d0",borderRadius:8,padding:"8px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:11,height:11,border:"2px solid #a7f3d0",borderTopColor:GRN,borderRadius:"50%",animation:"spin .7s linear infinite",flexShrink:0}}/>
                <span style={{fontSize:13,color:GRND,fontWeight:600,...DS}}>
                  {mealLoad?"Filtering by board type…":allRaw.length+` found — loading more (${pct}%)…`}
                </span>
              </div>
            )}

            {!isLoading&&error&&(
              <div style={{background:REDL,border:"1px solid #fecaca",borderRadius:10,padding:24,textAlign:"center"}}>
                <p style={{fontWeight:700,color:"#dc2626",marginBottom:8,...DS}}>Could not load results</p>
                <button onClick={()=>window.location.reload()} style={{padding:"9px 20px",background:NAVY,color:"#fff",border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Try again</button>
              </div>
            )}

            {!isLoading&&!error&&displayed.length===0&&allRaw.length>0&&(
              <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:12,padding:32,textAlign:"center"}}>
                <div style={{fontSize:36,marginBottom:10}}>🔍</div>
                <p style={{fontWeight:900,fontSize:16,color:NAVY,...PJS,marginBottom:6}}>No hotels match your filters</p>
                <button onClick={()=>setFilters({meals:[],stars:[],rating:"0",price:"",freeCan:false})} style={{padding:"11px 24px",background:NAVY,color:"#fff",border:"none",borderRadius:9,fontWeight:700,cursor:"pointer",fontFamily:"inherit",...DS}}>Clear all filters</button>
              </div>
            )}

            {!isLoading&&!error&&allRaw.length===0&&(
              <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:12,padding:32,textAlign:"center"}}>
                <div style={{fontSize:36,marginBottom:10}}>🧭</div>
                <p style={{fontWeight:900,fontSize:16,color:NAVY,...PJS,marginBottom:6}}>
                  {isAll?"No hotels available for these dates":`We couldn't find hotels for "${city}" on these dates`}
                </p>
                <p style={{fontSize:14,color:"#64748b",...DS,marginBottom:16}}>
                  Try a nearby town, a wider area (e.g. a region or country), or different dates — or call us and our team will find it for you.
                </p>
                <a href="tel:02030190055" style={{display:"inline-block",padding:"11px 24px",background:NAVY,color:"#fff",borderRadius:9,fontWeight:700,textDecoration:"none",...DS}}>Ask our travel team</a>
              </div>
            )}

            {displayed.map(({hotel:h,dest},i)=>(
              <div key={gId(h)||i}>
                {isAll&&(i===0||dest!==displayed[i-1]?.dest)&&(
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 0 8px",borderTop:i>0?`1px solid ${BDR}`:"none",marginTop:i>0?10:0}}>
                    <I.Pin s={13}/>
                    <span style={{fontWeight:800,fontSize:15,color:NAVY,...PJS}}>{dest}</span>
                    <span style={{color:"#94a3b8",fontSize:12,...DS}}>— {allRaw.filter(r=>r.dest===dest).length} hotels</span>
                  </div>
                )}
                <HotelCard h={h} city={city||dest} ci={ci} co={co} guests={guests} activeMeal={activeMeal}/>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer/>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes sk{0%{background-position:200% 0}100%{background-position:-200% 0}}
        .res-sidebar{display:block}
        .mob-btn{display:none!important}
        @media(max-width:1000px){.res-sidebar{display:none!important}.mob-btn{display:flex!important}}
        @media(max-width:640px){.hcard-inner{flex-direction:column!important}.hcard-img{width:100%!important;min-height:200px}.hcard-price{width:100%!important;border-left:none!important;border-top:1px solid #e2e8f0}}
      `}</style>
    </>
  );
}